import { createSlice } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";
import type {
  ICollaboratorData,
  IPersonaProject,
  ICategory,
  TranscriptMetadataStore,
  IPersona,
  IMessage,
} from "src/pages/Studies/models/interfaces";

function nowISO() {
  return new Date().toISOString();
}

function defaultCategory(): ICategory {
  return {
    personas: [],
  };
}

function timeValue(value?: string) {
  if (!value) return 0;
  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? 0 : parsed;
}

function isOlderProjectPayload(
  existing: IPersonaProject,
  incoming: IPersonaProject
) {
  const existingUpdatedAt = timeValue(existing.updatedAt);
  const incomingUpdatedAt = timeValue(incoming.updatedAt);
  return existingUpdatedAt > 0 && incomingUpdatedAt > 0 && incomingUpdatedAt < existingUpdatedAt;
}

function mergeProjectSummary(
  existing: IPersonaProject | undefined,
  incoming: IPersonaProject
) {
  if (!existing) return { ...incoming, chats: incoming.chats ?? [] };
  if (isOlderProjectPayload(existing, incoming)) return existing;

  return {
    ...existing,
    ...incoming,
    categories: existing.categories ?? incoming.categories,
    chats: existing.chats ?? incoming.chats ?? [],
  };
}

interface PersonasHomeState {
  personaProjects: IPersonaProject[];
  deletedChatIdsByProject: Record<string, string[]>;
  personasSearch: string;
  isLoading: boolean;
}

export const initialState: PersonasHomeState = {
  personaProjects: [],
  deletedChatIdsByProject: {},
  personasSearch: "",
  isLoading: false,
};

function mergeChat(
  existing: IPersonaProject["chats"][number] | undefined,
  incoming: IPersonaProject["chats"][number]
) {
  if (!existing) return incoming;
  if (timeValue(existing.updatedAt) > timeValue(incoming.updatedAt)) {
    return existing;
  }
  return incoming;
}

function mergeChats(
  existingChats: IPersonaProject["chats"] | undefined,
  incomingChats: IPersonaProject["chats"] | undefined,
  deletedChatIds: string[]
) {
  const deleted = new Set(deletedChatIds);
  const byId = new Map<string, IPersonaProject["chats"][number]>();

  (incomingChats ?? []).forEach((chat) => {
    if (!deleted.has(chat.id)) byId.set(chat.id, chat);
  });

  (existingChats ?? []).forEach((chat) => {
    if (deleted.has(chat.id)) return;
    byId.set(chat.id, mergeChat(chat, byId.get(chat.id) ?? chat));
  });

  return Array.from(byId.values()).sort((a, b) =>
    b.updatedAt.localeCompare(a.updatedAt)
  );
}

function mergeProjectDetail(
  existing: IPersonaProject | undefined,
  incoming: IPersonaProject,
  deletedChatIds: string[]
) {
  const incomingWithFilteredChats = {
    ...incoming,
    chats: (incoming.chats ?? []).filter(
      (chat) => !deletedChatIds.includes(chat.id)
    ),
  };

  if (!existing) return incomingWithFilteredChats;
  if (isOlderProjectPayload(existing, incoming)) return existing;

  return {
    ...existing,
    ...incomingWithFilteredChats,
    chats: mergeChats(existing.chats, incoming.chats, deletedChatIds),
  };
}

const personasHomeSlice = createSlice({
  name: "personasHomeSlice",
  initialState,
  reducers: {
    setPersonaProjects: (state, action: PayloadAction<IPersonaProject[]>) => {
      state.personaProjects = action.payload.map((project) =>
        mergeProjectSummary(
          state.personaProjects.find((existing) => existing.id === project.id),
          project
        )
      );
    },

    upsertPersonaProject: (state, action: PayloadAction<IPersonaProject>) => {
      const idx = state.personaProjects.findIndex(
        (p) => p.id === action.payload.id
      );
      const deletedChatIds =
        state.deletedChatIdsByProject[action.payload.id] ?? [];
      if (idx >= 0) {
        state.personaProjects[idx] = mergeProjectDetail(
          state.personaProjects[idx],
          action.payload,
          deletedChatIds
        );
      } else {
        state.personaProjects.unshift(
          mergeProjectDetail(undefined, action.payload, deletedChatIds)
        );
      }
    },

    deletePersonaProject: (state, action: PayloadAction<string>) => {
      state.personaProjects = state.personaProjects.filter(
        (p) => p.id !== action.payload
      );
    },

    renamePersonaProject: (
      state,
      action: PayloadAction<{ description?: string; id: string; name: string }>
    ) => {
      const project = state.personaProjects.find(
        (p) => p.id === action.payload.id
      );
      if (project) {
        project.name = action.payload.name;
        if (action.payload.description !== undefined) {
          project.description = action.payload.description;
        }
        project.updatedAt = nowISO();
      }
    },

    setPersonasSearch: (state, action: PayloadAction<string>) => {
      state.personasSearch = action.payload;
    },

    setPersonasLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },

    updatePersonaProjectCollaborators: (
      state,
      action: PayloadAction<{ project_id: string; collaborators: ICollaboratorData[] }>
    ) => {
      const { collaborators, project_id } = action.payload;
      const project = state.personaProjects.find((p) => p.id === project_id);
      if (project) {
        project.project_collaborators = collaborators;
      }
    },

    // ── Category / persona mutations ──────────────────────────────────────────

    ensureCategory: (
      state,
      action: PayloadAction<{
        projectId: string;
        category: "segment" | "setting";
      }>
    ) => {
      const project = state.personaProjects.find(
        (p) => p.id === action.payload.projectId
      );
      if (!project) return;
      const cat = project.categories[action.payload.category];
      if (!cat) {
        project.categories[action.payload.category] = defaultCategory();
      }
    },

    addPersona: (
      state,
      action: PayloadAction<{
        projectId: string;
        category: "segment" | "setting";
        persona: IPersona;
      }>
    ) => {
      const project = state.personaProjects.find(
        (p) => p.id === action.payload.projectId
      );
      if (!project) return;
      project.categories[action.payload.category].personas.push(
        action.payload.persona
      );
      project.updatedAt = nowISO();
    },

    updatePersona: (
      state,
      action: PayloadAction<{
        projectId: string;
        category: "segment" | "setting";
        personaId: string;
        updates: Partial<IPersona>;
      }>
    ) => {
      const project = state.personaProjects.find(
        (p) => p.id === action.payload.projectId
      );
      if (!project) return;
      const cat = project.categories[action.payload.category];
      const persona = cat.personas.find(
        (per) => per.id === action.payload.personaId
      );
      if (persona) {
        Object.assign(persona, action.payload.updates);
        project.updatedAt = nowISO();
      }
    },

    deletePersonaFromProject: (
      state,
      action: PayloadAction<{
        projectId: string;
        category: "segment" | "setting";
        personaId: string;
      }>
    ) => {
      const project = state.personaProjects.find(
        (p) => p.id === action.payload.projectId
      );
      if (!project) return;
      const cat = project.categories[action.payload.category];
      cat.personas = cat.personas.filter(
        (p) => p.id !== action.payload.personaId
      );
      project.updatedAt = nowISO();
    },

    setAttachedStudies: (
      state,
      action: PayloadAction<{
        projectId: string;
        category: "segment" | "setting";
        personaId: string;
        studyIds: string[];
        transcripts: Record<string, string[] | undefined>;
        metadata: TranscriptMetadataStore;
      }>
    ) => {
      const project = state.personaProjects.find(
        (p) => p.id === action.payload.projectId
      );
      if (!project) return;
      const cat = project.categories[action.payload.category];
      const persona = cat.personas.find(
        (p) => p.id === action.payload.personaId
      );
      if (!persona) return;
      persona.attachedStudyIds = action.payload.studyIds;
      persona.attachedTranscripts = action.payload.transcripts;
      persona.transcriptMetadata = action.payload.metadata;
      project.updatedAt = nowISO();
    },

    // ── Discourse mutations ───────────────────────────────────────────────────

    addChat: (
      state,
      action: PayloadAction<{
        projectId: string;
        chatId: string;
        category: "segment" | "setting";
        personaId: string;
      }>
    ) => {
      const project = state.personaProjects.find(
        (p) => p.id === action.payload.projectId
      );
      if (!project) return;
      project.chats ??= [];
      project.chats.unshift({
        id: action.payload.chatId,
        category: action.payload.category,
        personaId: action.payload.personaId,
        title: "New Discourse",
        saved: false,
        createdAt: nowISO(),
        updatedAt: nowISO(),
        messages: [],
      });
      project.updatedAt = nowISO();
    },

    addMessage: (
      state,
      action: PayloadAction<{
        projectId: string;
        chatId: string;
        message: IMessage;
      }>
    ) => {
      const project = state.personaProjects.find(
        (p) => p.id === action.payload.projectId
      );
      if (!project) return;
      project.chats ??= [];
      const chat = project.chats.find((c) => c.id === action.payload.chatId);
      if (!chat) return;
      chat.messages.push(action.payload.message);
      chat.updatedAt = nowISO();
    },

    updateChat: (
      state,
      action: PayloadAction<{
        projectId: string;
        chatId: string;
        updates: Partial<{
          personaId: string;
          title: string;
          saved: boolean;
          messages: IMessage[];
        }>;
      }>
    ) => {
      const project = state.personaProjects.find(
        (p) => p.id === action.payload.projectId
      );
      if (!project) return;
      project.chats ??= [];
      const chat = project.chats.find((c) => c.id === action.payload.chatId);
      if (!chat) return;
      Object.assign(chat, action.payload.updates);
      chat.updatedAt = nowISO();
    },

    setChatAttachedFile: (
      state,
      action: PayloadAction<{
        projectId: string;
        chatId: string;
        attachedFile: NonNullable<IPersonaProject["chats"][number]["attachedFile"]> | null;
      }>
    ) => {
      const project = state.personaProjects.find(
        (p) => p.id === action.payload.projectId
      );
      if (!project) return;
      project.chats ??= [];
      const chat = project.chats.find((c) => c.id === action.payload.chatId);
      if (!chat) return;
      chat.attachedFile = action.payload.attachedFile;
      chat.updatedAt = nowISO();
    },

    deleteChat: (
      state,
      action: PayloadAction<{ projectId: string; chatId: string }>
    ) => {
      const project = state.personaProjects.find(
        (p) => p.id === action.payload.projectId
      );
      if (!project) return;
      project.chats ??= [];
      state.deletedChatIdsByProject[action.payload.projectId] = [
        ...(state.deletedChatIdsByProject[action.payload.projectId] ?? []),
        action.payload.chatId,
      ];
      project.chats = project.chats.filter(
        (c) => c.id !== action.payload.chatId
      );
      project.updatedAt = nowISO();
    },

    resetPersonasHome: () => initialState,
  },
});

export const {
  addChat,
  addMessage,
  addPersona,
  deleteChat,
  deletePersonaFromProject,
  deletePersonaProject,
  ensureCategory,
  renamePersonaProject,
  resetPersonasHome,
  setAttachedStudies,
  setChatAttachedFile,
  setPersonaProjects,
  setPersonasLoading,
  setPersonasSearch,
  updateChat,
  updatePersona,
  updatePersonaProjectCollaborators,
  upsertPersonaProject,
} = personasHomeSlice.actions;
export default personasHomeSlice.reducer;
